import { ArrowDownToLine, Github, Linkedin, Mail } from "lucide-react";

import { profile } from "@/data/profile";
import { useLocale } from "@/i18n/locale";
import { ui } from "@/i18n/ui";

export function Hero() {
  const { t } = useLocale();

  return (
    <section id="top" className="relative overflow-hidden">
      <div aria-hidden="true" className="grid-backdrop absolute inset-0 -z-10" />

      <div className="mx-auto w-full max-w-5xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-24">
        <p className="label mb-6 text-accent">{t(ui.availableForWork)}</p>

        <h1 className="text-4xl font-medium tracking-tighter sm:text-6xl">{profile.name}</h1>

        <p className="mt-3 font-mono text-sm text-muted sm:text-base">
          {profile.role} — {profile.disciplines.join(" · ")}
        </p>

        <p className="mt-8 max-w-2xl text-base leading-relaxed text-muted text-pretty sm:text-lg">
          {t(profile.intro)}
        </p>

        <p className="mt-4 max-w-2xl text-base leading-relaxed text-pretty sm:text-lg">
          <span className="text-muted">{t(ui.knownFor)}</span>
          <span className="text-text">{t(profile.hook)}</span>
        </p>

        <p className="mt-8 font-mono text-xs text-faint">
          {t(profile.location)} · {t(profile.availability)} ·{" "}
          {profile.languages.map((l) => t(l.name)).join(" / ")}
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href={`mailto:${profile.email}`}
            className="inline-flex items-center gap-2 rounded-sm border border-accent bg-accent px-4 py-2.5 text-sm font-medium text-bg transition-opacity hover:opacity-90"
          >
            <Mail aria-hidden="true" className="size-4" />
            {profile.email}
          </a>
          <a
            href={profile.linkedin}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-2 rounded-sm border border-border bg-surface px-4 py-2.5 text-sm text-muted transition-colors hover:border-border-strong hover:text-text"
          >
            <Linkedin aria-hidden="true" className="size-4" />
            LinkedIn
          </a>
          <a
            href={profile.githubPrimary}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-2 rounded-sm border border-border bg-surface px-4 py-2.5 text-sm text-muted transition-colors hover:border-border-strong hover:text-text"
          >
            <Github aria-hidden="true" className="size-4" />
            GitHub
          </a>
          <a
            href={profile.resumePath}
            download
            className="inline-flex items-center gap-2 rounded-sm border border-border bg-surface px-4 py-2.5 text-sm text-muted transition-colors hover:border-border-strong hover:text-text"
          >
            <ArrowDownToLine aria-hidden="true" className="size-4" />
            {t(ui.resume)}
          </a>
        </div>

        <dl className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-border bg-border sm:grid-cols-4">
          {profile.stats.map((stat) => (
            <div key={stat.label.en} className="bg-surface px-4 py-4">
              <dt className="label text-faint">{t(stat.label)}</dt>
              <dd className="mt-1.5 font-mono text-sm text-text">{t(stat.value)}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
